import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { topLevelFeatures, type FeatureId } from '@sequesto-os/shared';
import { useAuth } from './hooks/useAuth';
import { useAccess } from './hooks/useAccess';
import { Alert } from './components/ui/Alert';
import { AppShell } from './components/layout/AppShell';
import { Login } from './pages/Login';
import { Home } from './pages/Home';
import { Csm } from './pages/CSM';
import { Sales } from './pages/Sales';
import { Finance } from './pages/Finance';
import { CustomerDetailPage } from './pages/ProjectManagement/Finance/CustomerDetailPage';
import { Organizer } from './pages/Organizer';
import { Marketing } from './pages/Marketing';
import { GTM } from './pages/GTM';
import { Integrations } from './pages/Integrations';
import { Settings } from './pages/Settings';
import { Automations } from './pages/Automations';
import { ContentReviewPage } from './pages/Automations/ContentReviewPage';
import { Users } from './pages/Users';
import { James } from './pages/James';
import { JamesReport } from './pages/James/Report';
import { Signatures } from './pages/Signatures';
import { Resources } from './pages/Resources';
import { Documents } from './pages/Documents';
import { AcceptInvite } from './pages/AcceptInvite';
import { OAuthCallback } from './pages/OAuthCallback';
import { NotFound } from './pages/NotFound';
import { ErrorState } from './components/ui/States';

/**
 * While the session is checked: the app's frame (sidebar, header, page area)
 * in place, so a full reload does not flash a blank page with a spinner and
 * then jump to the shell.
 */
function ShellSkeleton() {
  return (
    <div className="flex h-screen bg-surface-secondary" aria-busy="true" aria-label="Loading Sequesto OS">
      <div className="h-full w-64 flex-none border-r border-border-light bg-surface-secondary px-3 py-5">
        <div className="h-5 w-32 rounded-sm bg-surface-tertiary" />
        <div className="mt-6 h-9 w-full rounded-xl bg-surface-tertiary/70" />
        <div className="mt-6 space-y-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="h-4 animate-pulse rounded-sm bg-surface-tertiary/70" style={{ width: `${55 + ((i * 13) % 35)}%` }} />
          ))}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="h-14 flex-none border-b border-border-light" />
        <div className="flex-1 px-6 py-8">
          <div className="h-8 w-56 animate-pulse rounded-sm bg-surface-tertiary/70" />
        </div>
      </div>
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading, bootError, retryBootstrap } = useAuth();

  if (isLoading) return <ShellSkeleton />;

  // The session couldn't be checked because the API is down or slow — that is
  // not "signed out", so say so and offer a retry instead of a login form.
  if (bootError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-secondary px-4">
        <div className="w-full max-w-md">
          <ErrorState
            title="Sequesto OS can't reach its API"
            error={bootError}
            onRetry={() => void retryBootstrap()}
          />
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/** Blocks a route the user can't reach (direct-URL access), mirroring the sidebar gating. */
function FeatureGuard({ feature, children }: { feature: FeatureId; children: React.ReactNode }) {
  const { canAccessArea } = useAccess();

  if (!canAccessArea(feature)) {
    return (
      <div className="p-8">
        <Alert variant="error">You don't have permission to access this page.</Alert>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * `/` is Home, but not everyone has Home: a Sales-only user would log in onto a
 * permission error. Send them to the first area they can reach instead.
 */
function LandingRoute() {
  const { canAccessArea } = useAccess();
  if (canAccessArea('home')) return <Home />;
  const first = topLevelFeatures().find(
    (f) => f.navPath && f.navPath !== '/' && canAccessArea(f.id as FeatureId),
  );
  return <Navigate to={first?.navPath ?? '/settings'} replace />;
}

// ─── Legacy redirects (preserve old bookmarks/links after the department reorg) ───

/** Old `/project-management/sales/:tab` → `/sales/ae/:tab`. */
function LegacySalesRedirect() {
  const { tab } = useParams<{ tab?: string }>();
  return <Navigate to={`/sales/ae/${tab ?? 'overview'}`} replace />;
}

/** Old `/project-management/sdr/:tab` → `/sales/sdr/:tab`. */
function LegacySdrRedirect() {
  const { tab } = useParams<{ tab?: string }>();
  return <Navigate to={`/sales/sdr/${tab ?? 'overview'}`} replace />;
}

/** Old `/project-management/finance/:tab` → `/finance/:tab`. */
function LegacyFinanceRedirect() {
  const { tab } = useParams<{ tab?: string }>();
  return <Navigate to={`/finance/${tab ?? 'overview'}`} replace />;
}

/** Old `/automations/content-engine/*` → `/marketing/content/*`. */
function LegacyContentEngineRedirect() {
  const params = useParams();
  const rest = params['*'] ?? '';
  const target = `/marketing/content${rest ? `/${rest}` : ''}`;
  return <Navigate to={target} replace />;
}

/** Old `/automations/content/:contentItemId` review deep-link → new content library path. */
function LegacyContentReviewRedirect() {
  const { contentItemId } = useParams<{ contentItemId: string }>();

  if (!contentItemId) {
    return <Navigate to="/marketing/content/library" replace />;
  }

  return <Navigate to={`/marketing/content/library/${contentItemId}`} replace />;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route
        path="/*"
        element={
          <PrivateRoute>
            <AppShell>
              <Routes>
                <Route path="/oauth/callback" element={<OAuthCallback />} />

                {/* Home */}
                <Route path="/" element={<LandingRoute />} />

                {/* Sales — Account Executives + SDRs */}
                <Route path="/sales" element={<FeatureGuard feature="sales"><Sales /></FeatureGuard>} />
                <Route path="/sales/:section" element={<FeatureGuard feature="sales"><Sales /></FeatureGuard>} />
                <Route path="/sales/:section/:tab" element={<FeatureGuard feature="sales"><Sales /></FeatureGuard>} />

                {/* CSM */}
                <Route path="/csm" element={<FeatureGuard feature="csm"><Csm /></FeatureGuard>} />
                <Route path="/csm/:tab" element={<FeatureGuard feature="csm"><Csm /></FeatureGuard>} />

                {/* GTM — Signal Ops: the canonical record, signals, scoring, attribution */}
                <Route path="/gtm" element={<FeatureGuard feature="gtm"><GTM /></FeatureGuard>} />
                <Route path="/gtm/:section" element={<FeatureGuard feature="gtm"><GTM /></FeatureGuard>} />

                {/* Marketing — AI Visibility · GTM dashboard · Content Engine (incl. LinkedIn posts) */}
                <Route path="/marketing" element={<FeatureGuard feature="marketing"><Marketing /></FeatureGuard>} />
                <Route path="/marketing/content/library/:contentItemId" element={<FeatureGuard feature="marketing"><ContentReviewPage /></FeatureGuard>} />
                <Route path="/marketing/:section/*" element={<FeatureGuard feature="marketing"><Marketing /></FeatureGuard>} />

                {/* Finance */}
                <Route path="/finance" element={<FeatureGuard feature="finance"><Finance /></FeatureGuard>} />
                <Route path="/finance/customers/:id" element={<FeatureGuard feature="finance"><CustomerDetailPage /></FeatureGuard>} />
                <Route path="/finance/:tab" element={<FeatureGuard feature="finance"><Finance /></FeatureGuard>} />

                {/* Personal — private to-do board, call-derived suggestions, reminders */}
                <Route path="/organizer" element={<FeatureGuard feature="organizer"><Organizer /></FeatureGuard>} />
                {/* Reminders was a static explainer tab; its content now lives in Settings › Notifications */}
                <Route path="/organizer/reminders" element={<Navigate to="/settings" replace />} />
                <Route path="/organizer/:section" element={<FeatureGuard feature="organizer"><Organizer /></FeatureGuard>} />
                <Route path="/organizer/:section/:taskId" element={<FeatureGuard feature="organizer"><Organizer /></FeatureGuard>} />

                {/* James */}
                {/* Resources — the link register. `:section` is the feature id of
                    the open tab, so a section is a shareable link. */}
                <Route path="/resources" element={<FeatureGuard feature="resources"><Resources /></FeatureGuard>} />
                <Route path="/resources/:section" element={<FeatureGuard feature="resources"><Resources /></FeatureGuard>} />

                {/* On-brand documents — upload, rebuild on the document system, review */}
                <Route path="/documents" element={<FeatureGuard feature="documents"><Documents /></FeatureGuard>} />

                <Route path="/james" element={<FeatureGuard feature="james"><James /></FeatureGuard>} />
                <Route path="/james/reports/:reportId" element={<FeatureGuard feature="james"><JamesReport /></FeatureGuard>} />
                <Route path="/james/:threadId" element={<FeatureGuard feature="james"><James /></FeatureGuard>} />

                {/* Automations — background scripts/jobs only */}
                <Route path="/automations" element={<FeatureGuard feature="automations"><Automations /></FeatureGuard>} />

                {/* Account */}
                <Route path="/integrations" element={<FeatureGuard feature="integrations"><Integrations /></FeatureGuard>} />
                <Route path="/users" element={<FeatureGuard feature="users"><Users /></FeatureGuard>} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/signatures" element={<FeatureGuard feature="signatures"><Signatures /></FeatureGuard>} />

                {/* ── Legacy path redirects ── */}
                {/* `/gtm` used to redirect to the Marketing GTM dashboard. It is now the
                    GTM department root (declared above). The Marketing GTM dashboard is
                    unchanged and still lives at /marketing/gtm. */}
                <Route path="/project-management" element={<Navigate to="/sales" replace />} />
                <Route path="/project-management/sales" element={<Navigate to="/sales/ae/overview" replace />} />
                <Route path="/project-management/sales/:tab" element={<LegacySalesRedirect />} />
                <Route path="/project-management/sdr" element={<Navigate to="/sales/sdr/overview" replace />} />
                <Route path="/project-management/sdr/:tab" element={<LegacySdrRedirect />} />
                <Route path="/project-management/finance" element={<Navigate to="/finance/overview" replace />} />
                <Route path="/project-management/finance/:tab" element={<LegacyFinanceRedirect />} />
                <Route path="/automations/scripts" element={<Navigate to="/automations" replace />} />
                <Route path="/automations/linkedin-posts" element={<Navigate to="/marketing/content/linkedin" replace />} />
                <Route path="/marketing/linkedin" element={<Navigate to="/marketing/content/linkedin" replace />} />
                <Route path="/automations/content/:contentItemId" element={<LegacyContentReviewRedirect />} />
                <Route path="/automations/content-engine/*" element={<LegacyContentEngineRedirect />} />

                {/* Anything else: a plain "Page not found", not a blank page */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AppShell>
          </PrivateRoute>
        }
      />
    </Routes>
  );
}
